import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import {
  ShippingMethodAutoInvite,
  ShippingMethodStatus,
} from '@invenco/common-domain/enums/ShippingMethod';
import type {
  CreateWarehouseShippingMethodDTO,
  ShippingMethodDTO,
} from '@invenco/shipping-interface';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/core/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/core/select';
import { useWhileDialogOpen } from '@/components/dialog-with-context';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { SwitchFormField } from '@/components/forms/switch-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { Result } from '@/shared/helpers/Result';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { shippingMethodAutoInviteTitle, shippingMethodStatusTitle } from '@/shared/title-maps';

const formSchema = z.object({
  carrierServiceId: z.string().min(1, 'Please select a carrier service'),
  status: z.enum(ShippingMethodStatus),
  ratesCarrierAccountId: z.string().min(1, 'Please select rates carrier account'),
  bookingCarrierAccountId: z.string().min(1, 'Please select booking carrier account'),
  name: z.string().min(1, 'Please enter a name'),
  autoInvite: z.enum(ShippingMethodAutoInvite),
  dynamicallyPriced: z.boolean(),
  // Edit-only: whether saving skips cascading the changes to managed children (mirrors the wire
  // `skip_managed_cascade`). Always present in the form values (defaults false = cascade); only
  // forwarded to the PATCH body in edit mode by the consumer.
  skipManagedCascade: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

type Mode = 'add' | 'edit';

const dialogTitleByMode: Record<Mode, string> = {
  add: 'Add warehouse shipping method',
  edit: 'Edit warehouse shipping method',
};

const dialogDescriptionByMode: Record<Mode, string> = {
  add: 'Configure a carrier service for this warehouse location.',
  edit: 'Update the settings for this warehouse shipping method.',
};

export type WarehouseShippingMethodFormValues = Omit<
  CreateWarehouseShippingMethodDTO,
  'accountId' | 'locationId' | 'type'
> & {
  // Not part of the create DTO — it only applies to the warehouse PATCH (edit mode). It rides on
  // the form values so the consumer can forward it on edit while ignoring it on add.
  skipManagedCascade: boolean;
};

type Props = {
  accountId: string;
  /** Whether the form creates a new shipping method or edits an existing one. */
  mode: Mode;
  /** The shipping method to prefill the form with in edit mode. */
  shippingMethod?: ShippingMethodDTO;
  existingShippingMethods: ShippingMethodDTO[];
  /**
   * Receives the validated form values. A single handler serves both modes — the
   * consumer owns `mode` and decides how to persist them (create vs. update),
   * so this component stays agnostic about the destination.
   */
  onSubmit: (values: WarehouseShippingMethodFormValues) => Promise<Result>;
};

export function WarehouseShippingMethodDialogContent({
  accountId,
  mode,
  shippingMethod,
  existingShippingMethods,
  onSubmit,
}: Props) {
  const { shippingGateway } = useGateways();

  // Stabilise mode + DTO for the dialog's lifetime so closing (or reopening with a
  // different row) doesn't flicker stale content into the form mid-animation.
  const stableMode = useWhileDialogOpen(mode);
  const stableShippingMethod = useWhileDialogOpen(shippingMethod);

  const defaultValues = useMemo<FormValues>(
    () => ({
      carrierServiceId: stableShippingMethod?.carrierServiceId ?? '',
      status: stableShippingMethod?.status ?? ShippingMethodStatus.ACTIVE,
      ratesCarrierAccountId: stableShippingMethod?.ratesCarrierAccountId ?? '',
      bookingCarrierAccountId: stableShippingMethod?.bookingCarrierAccountId ?? '',
      name: stableShippingMethod?.name ?? '',
      autoInvite: stableShippingMethod?.autoInvite ?? ShippingMethodAutoInvite.MANAGED_LOCATIONS,
      dynamicallyPriced: stableShippingMethod?.dynamicallyPriced ?? false,
      skipManagedCascade: false,
    }),
    [
      stableShippingMethod?.carrierServiceId,
      stableShippingMethod?.status,
      stableShippingMethod?.ratesCarrierAccountId,
      stableShippingMethod?.bookingCarrierAccountId,
      stableShippingMethod?.name,
      stableShippingMethod?.autoInvite,
      stableShippingMethod?.dynamicallyPriced,
    ],
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const { data: carrierServices = [], isLoading: isLoadingCarrierServices } = useQuery({
    queryKey: ['carrierServices'],
    queryFn: ({ signal }) => shippingGateway.getCarrierServices(undefined, { signal }),
    select: ({ items }) => items,
  });

  const availableCarrierServices = useMemo(() => {
    // Exclude carrier services already taken by other methods, but keep the one being
    // edited so its prefilled selection stays visible in the dropdown.
    const usedCarrierServiceIds = new Set(
      existingShippingMethods
        .filter((method) => method.id !== stableShippingMethod?.id)
        .map((method) => method.carrierServiceId),
    );

    return carrierServices.filter(
      (carrierService) => !usedCarrierServiceIds.has(carrierService.id),
    );
  }, [carrierServices, existingShippingMethods, stableShippingMethod?.id]);

  const selectedCarrierServiceId = form.watch('carrierServiceId');
  const selectedCarrierService = useMemo(
    () =>
      availableCarrierServices.find(
        (carrierService) => carrierService.id === selectedCarrierServiceId,
      ),
    [availableCarrierServices, selectedCarrierServiceId],
  );

  useEffect(() => {
    // Only auto-populate the name from the carrier service when adding. In edit mode this
    // would clobber a user-set name when the carrier service field changes.
    if (stableMode !== 'add' || !selectedCarrierService) {
      return;
    }

    form.setValue('name', selectedCarrierService.name, { shouldDirty: true, shouldValidate: true });
  }, [form, selectedCarrierService, stableMode]);

  const { data: carrierAccounts = [], isLoading: isLoadingCarrierAccounts } = useQueryWithInput({
    parentKey: 'carrierAccounts',
    input: {
      accountId,
      carrierIds: selectedCarrierService?.carrierId
        ? [selectedCarrierService.carrierId]
        : undefined,
    },
    query: (input, { signal }) => shippingGateway.getCarrierAccounts(input, { signal }),
    select: ({ items }) => items,
    enabled: Boolean(accountId),
  });

  const hasAvailableCarrierServices = availableCarrierServices.length > 0;
  const noCarrierServicesAvailable =
    stableMode === 'add' && !isLoadingCarrierServices && !hasAvailableCarrierServices;

  // The backend update use case (UpdateShippingMethod) does not accept carrierServiceId or
  // name, so those are locked in edit mode — shown for context but not editable.
  const isEditMode = stableMode === 'edit';

  // Warehouse-edit-only control deciding whether the save cascades to managed children. Rendered
  // in the footer (left of Cancel / Save). The field holds the skip flag, so the "don't skip"
  // option (false) is cascade-on; convert at the Select's string value boundary. Kept inline
  // rather than generalising SelectFormField for one site.
  const cascadeFooterControl = isEditMode ? (
    <FormField
      control={form.control}
      name="skipManagedCascade"
      render={({ field }) => (
        <FormItem>
          <Select
            value={String(field.value)}
            onValueChange={(value) => field.onChange(value === 'true')}
            name={field.name}
          >
            <FormControl>
              <SelectTrigger
                className="w-56"
                aria-label="Cascade changes to managed shipping methods"
              >
                <SelectValue />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="false">Update all child records</SelectItem>
              <SelectItem value="true">Only apply to new records</SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  ) : undefined;

  return (
    <FormDialogContent
      form={form}
      side="right"
      submitLabel={stableMode === 'edit' ? 'Save' : 'Add'}
      isSubmitDisabled={stableMode === 'add' && !hasAvailableCarrierServices}
      footerStart={cascadeFooterControl}
      onSubmit={async (data) =>
        onSubmit({
          name: data.name,
          carrierServiceId: data.carrierServiceId,
          status: data.status,
          ratesCarrierAccountId: data.ratesCarrierAccountId,
          bookingCarrierAccountId: data.bookingCarrierAccountId,
          autoInvite: data.autoInvite,
          dynamicallyPriced: data.dynamicallyPriced,
          skipManagedCascade: data.skipManagedCascade,
        })
      }
    >
      <DialogHeader>
        <DialogTitle>{dialogTitleByMode[stableMode]}</DialogTitle>
        <DialogDescription>
          {noCarrierServicesAvailable
            ? 'All available carrier services have already been added'
            : dialogDescriptionByMode[stableMode]}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <SelectFormField
          control={form.control}
          name="carrierServiceId"
          label="Carrier service"
          placeholder={
            isLoadingCarrierServices ? 'Loading carrier services...' : 'Select a carrier service'
          }
          disabled={isEditMode || isLoadingCarrierServices || !hasAvailableCarrierServices}
          className="w-full"
        >
          {availableCarrierServices.map((carrierService) => (
            <SelectItem key={carrierService.id} value={carrierService.id}>
              {carrierService.name}
            </SelectItem>
          ))}
          {!availableCarrierServices.length && !isLoadingCarrierServices ? (
            <SelectItem value="no-carrier-services" disabled>
              No available carrier services
            </SelectItem>
          ) : null}
        </SelectFormField>

        <SelectFormField
          control={form.control}
          name="status"
          label="Status"
          placeholder="Select status"
          disabled={!hasAvailableCarrierServices}
          className="w-full"
        >
          {Object.values(ShippingMethodStatus).map((status) => (
            <SelectItem key={status} value={status}>
              {shippingMethodStatusTitle[status]}
            </SelectItem>
          ))}
        </SelectFormField>

        <SelectFormField
          control={form.control}
          name="ratesCarrierAccountId"
          label="Rates carrier account"
          placeholder={
            isLoadingCarrierAccounts ? 'Loading carrier accounts...' : 'Select a carrier account'
          }
          disabled={isLoadingCarrierAccounts || !hasAvailableCarrierServices}
          className="w-full"
        >
          {carrierAccounts.map((carrierAccount) => (
            <SelectItem key={carrierAccount.id} value={carrierAccount.id}>
              {carrierAccount.name}
            </SelectItem>
          ))}
        </SelectFormField>

        <SelectFormField
          control={form.control}
          name="bookingCarrierAccountId"
          label="Booking carrier account"
          placeholder={
            isLoadingCarrierAccounts ? 'Loading carrier accounts...' : 'Select a carrier account'
          }
          disabled={isLoadingCarrierAccounts || !hasAvailableCarrierServices}
          className="w-full"
        >
          {carrierAccounts.map((carrierAccount) => (
            <SelectItem key={carrierAccount.id} value={carrierAccount.id}>
              {carrierAccount.name}
            </SelectItem>
          ))}
        </SelectFormField>

        <TextFormField
          control={form.control}
          name="name"
          label="Name"
          disabled={isEditMode || !hasAvailableCarrierServices}
        />

        <SelectFormField
          control={form.control}
          name="autoInvite"
          label="Auto invite"
          disabled={!hasAvailableCarrierServices}
          className="w-full"
        >
          {Object.values(ShippingMethodAutoInvite).map((autoInvite) => (
            <SelectItem key={autoInvite} value={autoInvite}>
              {shippingMethodAutoInviteTitle[autoInvite]}
            </SelectItem>
          ))}
        </SelectFormField>

        <SwitchFormField
          control={form.control}
          name="dynamicallyPriced"
          label="Dynamically priced"
          disabled={!hasAvailableCarrierServices}
        />
      </DialogBody>
    </FormDialogContent>
  );
}
